import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Waves, 
  UtensilsCrossed, 
  Mountain, 
  Wifi, 
  Car, 
  Dog,
  Wind,
  Bed,
  Bath,
  Sun,
  TreePine,
  Shield,
  Clock,
  Users,
  ArrowRight,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';

const VILLA_HERO = "https://images.unsplash.com/photo-1758396610696-79f943a7fcfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2aWxsYSUyMHBvb2wlMjBuYXR1cmV8ZW58MHx8fHwxNzc1MDc5MTgxfDA&ixlib=rb-4.1.0&q=85";
const POOL_IMAGE = "https://images.unsplash.com/photo-1774280960001-ce8169cfc38f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB2aWxsYSUyMHBvb2wlMjBuYXR1cmV8ZW58MHx8fHwxNzc1MDc5MTgxfDA&ixlib=rb-4.1.0&q=85";
const BEDROOM_IMAGE = "https://images.unsplash.com/photo-1768487422639-7ba3900d0f02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxydXN0aWMlMjBlbGVnYW50JTIwYmVkcm9vbXxlbnwwfHx8fDE3NzUwNzkxODF8MA&ixlib=rb-4.1.0&q=85";
const HACIENDA_IMAGE = "https://images.pexels.com/photos/14399423/pexels-photo-14399423.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const amenities = [
  { icon: Waves, title: 'Alberca Privada', desc: 'Refréscate en nuestra alberca exclusiva con vista a las montañas' },
  { icon: UtensilsCrossed, title: 'Cocina Equipada', desc: 'Todo lo necesario para preparar tus comidas favoritas' },
  { icon: Mountain, title: 'Terraza Panorámica', desc: 'Disfruta de atardeceres inolvidables desde la terraza' },
  { icon: Wifi, title: 'WiFi Alta Velocidad', desc: 'Mantente conectado con internet de alta velocidad' },
  { icon: Car, title: 'Estacionamiento', desc: 'Amplio estacionamiento privado y seguro' },
  { icon: Dog, title: 'Pet Friendly', desc: 'Trae a tu mascota y disfruten juntos (cargo adicional)' },
  { icon: Wind, title: 'Aire Acondicionado', desc: 'Climatización en todas las habitaciones' },
  { icon: Sun, title: 'Área de Asador', desc: 'Parrilla equipada para tus reuniones' },
  { icon: TreePine, title: '2,000 m² de Jardín', desc: 'Extensas áreas verdes para relajarte' },
  { icon: Shield, title: 'Acceso Seguro', desc: 'Privacidad y seguridad garantizada' },
];

const rooms = [
  { icon: Bed, label: '3 Recámaras', desc: 'Camas king y queen size' },
  { icon: Bath, label: '2 Baños Completos', desc: 'Con amenidades de lujo' },
  { icon: Users, label: 'Hasta 10 Huéspedes', desc: 'Espacio para toda la familia' },
];

const rules = [
  'Check-in: 3:00 PM',
  'Check-out: 12:00 PM',
  'No fiestas sin autorización previa',
  'Horario de ruido: hasta las 10:00 PM',
  'No fumar dentro de la villa',
  'Mascotas con previo aviso',
  'Respetar la flora y fauna',
  'Máximo de huéspedes según reserva',
];

export default function VillaPage() {
  return (
    <div data-testid="villa-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${VILLA_HERO})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        </div>

        <div className="relative h-full flex flex-col justify-end pb-16 px-4">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
                Hospedaje Exclusivo
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
                Villa de Descanso
              </h1>
              <p className="text-white/80 text-lg max-w-2xl">
                Tu refugio privado en medio de la naturaleza. 2,000 m² de tranquilidad 
                con todas las comodidades que mereces.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-[#5C4033] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '10', label: 'Huéspedes' },
              { value: '3', label: 'Recámaras' },
              { value: '2', label: 'Baños' },
              { value: '2,000', label: 'm² de terreno' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-serif text-3xl md:text-4xl text-white">{stat.value}</p>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                Un Espacio Único para Descansar
              </h2>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-6">
                Ubicada en San Cayetano, Nayarit, nuestra villa ofrece el escape perfecto 
                de la rutina diaria. Con vistas impresionantes a las montañas y rodeada 
                de naturaleza, es el lugar ideal para reconectar con lo que realmente importa.
              </p>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-8">
                Ya sea que busques una escapada romántica, unas vacaciones familiares o 
                un retiro con amigos, nuestra villa tiene todo lo que necesitas para 
                una estancia inolvidable.
              </p>

              <div className="grid grid-cols-3 gap-4">
                {rooms.map((room) => (
                  <div
                    key={room.label}
                    className="text-center p-4 bg-white rounded-xl shadow-sm"
                  >
                    <room.icon className="h-8 w-8 text-[#C8962D] mx-auto mb-2" />
                    <p className="font-medium text-[#1A1A1A]">{room.label}</p>
                    <p className="text-xs text-[#1A1A1A]/60">{room.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={POOL_IMAGE}
                    alt="Pool"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={HACIENDA_IMAGE}
                    alt="Exterior"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="pt-8">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={BEDROOM_IMAGE}
                    alt="Bedroom"
                    className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Todo lo que necesitas
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A]">
              Amenidades
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {amenities.map((amenity, index) => (
              <motion.div
                key={amenity.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-[#FAF7F2] rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <amenity.icon className="h-8 w-8 text-[#C8962D] mx-auto mb-3" />
                <h3 className="font-medium text-[#1A1A1A] mb-1">{amenity.title}</h3>
                <p className="text-xs text-[#1A1A1A]/60">{amenity.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modalities & Rules */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Modalities */}
            <div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mb-8">
                Modalidades de Renta
              </h2>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-3">
                    <Clock className="h-6 w-6 text-[#C8962D] mr-3" />
                    <h3 className="font-serif text-xl text-[#1A1A1A]">Por Noche</h3>
                  </div>
                  <p className="text-[#1A1A1A]/70">
                    Estancia mínima de 1 noche. Ideal para escapadas de fin de semana 
                    o vacaciones cortas. Check-in 3:00 PM, Check-out 12:00 PM.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-3">
                    <Sun className="h-6 w-6 text-[#C8962D] mr-3" />
                    <h3 className="font-serif text-xl text-[#1A1A1A]">Renta de Día</h3>
                  </div>
                  <p className="text-[#1A1A1A]/70">
                    Uso de la villa y todas las amenidades de 9:00 AM a 6:00 PM. 
                    Perfecto para reuniones, cumpleaños o un día de alberca.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-3">
                    <Users className="h-6 w-6 text-[#C8962D] mr-3" />
                    <h3 className="font-serif text-xl text-[#1A1A1A]">Estancia Extendida</h3>
                  </div>
                  <p className="text-[#1A1A1A]/70">
                    Reserva 3 o más noches y obtén tarifas preferenciales. 
                    Descuentos especiales para estancias de una semana o más.
                  </p>
                </div>
              </div>
            </div>

            {/* House Rules */}
            <div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mb-8">
                Reglas de la Casa
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <ul className="space-y-4">
                  {rules.map((rule) => (
                    <li key={rule} className="flex items-start">
                      <Check className="h-5 w-5 text-[#3D5A3E] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-[#1A1A1A]/70">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5C4033]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            ¿Listo para tu escapada?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Reserva ahora y vive la experiencia La Finca
          </p>
          <Link to="/reservar">
            <Button
              data-testid="villa-cta-reserve-btn"
              size="lg"
              className="bg-[#C8962D] hover:bg-[#B8861D] text-white px-12"
            >
              Reservar Ahora
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
